import { useLocalStorage } from "@mantine/hooks"
import * as Sentry from "@sentry/browser"
import { AppShell, Button, FormSelector, Selector } from "@vesatogo/grass-core"
import classNames from "classnames"
import { useEffect, useState } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import BizLogo from "~/assets/vesatogo.png"
import { AuthorizationProvider } from "~/components/AuthorizationProvider"
import { Permission } from "~/constants/Permissions"
import { AppRoutes } from "~/constants/routes"
import { resetFilter } from "~/utils/filterUtils"
import { useAuthenticatedUserQuery } from "../generated/graphql"
import { dashboardRoutes } from "../routes/dashboard.routes"
import { useURQLClient } from "../services/urql.service"
import { List } from "@phosphor-icons/react"

const permissionsList = Object.values(Permission)

const viewOptions = [
  { id: "1", name: "Commodity" },
  { id: "2", name: "Packaging" },
]
const DashboardLayout = ({ contentClassName = undefined }: any) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const [{ data }] = useAuthenticatedUserQuery()
  const isPlantSettlement = pathname.includes(AppRoutes.plantSettlement)
  const isUploaderTask = pathname.includes(AppRoutes.uploaderTask)
  const isCredit = pathname.includes(AppRoutes.credit)
  const isStock = pathname.includes(AppRoutes.stockManagement)
  const isTradeRequest = pathname === AppRoutes.tradeRequests
  const isStockManagement = pathname.includes(AppRoutes.stockManagement)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const { resetClient } = useURQLClient()
  const me = data?.me

  const navLinks = dashboardRoutes
    .filter(route => !route.hide)
    .filter(route => {
      return route.permission
        ? data?.me?.permissions
            ?.map(permission => permission?.split(".")[1])
            ?.includes(route.permission)
        : true
    })
    .map(route => {
      return {
        renderLink: icon => (
          <Link
            onClick={() => {
              resetFilter()
              setIsSidebarOpen(false)
            }}
            to={route.path?.toString()}
            state={{ fromSidebar: true }}
          >
            {icon}
          </Link>
        ),
        id: route.path,
        title: route.title,
        icon: route.icon,
        isActive: pathname.includes(route.path),
      }
    })

  const [department, setDepartment] = useLocalStorage({ key: "department" })
  const canViewPackagingSelection = data?.me?.permissions
    ?.map(permission => permission?.split(".")[1])
    ?.includes(Permission.CanViewPackagingSelection)

  const [viewPackaging, setViewPackaging] = useLocalStorage({
    key: "viewPackaging",
    defaultValue: "1",
  })

  const departments = data?.me?.agent?.departments?.map(
    item => item?.department
  )

  useEffect(() => {
    if (pathname === "/d") {
      navigate("trade-requests")
    }
  }, [pathname, navigate])

  useEffect(() => {
    if (!localStorage.getItem("token") && !data?.me?.id) {
      navigate("/")
    }
    if (!canViewPackagingSelection) {
      setViewPackaging("1")
    }
  }, [data])

  useEffect(() => {
    if (me?.id) {
      Sentry.setUser({
        id: me.id,
        number: me.number,
        username: me.full_name as string,
        email: me.email,
        segment: import.meta.env.VITE_API_URL,
      })
    }
  }, [me])
  return (
    <AuthorizationProvider permissions={permissionsList}>
      <AppShell
        logoUrl={BizLogo}
        links={navLinks}
        className={classNames(
          "[&>nav]:overflow-visible",
          "max-sm:[&>section]:!ml-0",
          isSidebarOpen
            ? "[&>nav]:flex [&>nav]:fixed sm:[&>nav]:!static [&>nav]:z-50 sm:[&>nav]:z-20 [&>nav]:h-[100dvh] sm:[&>nav]:!h-auto"
            : "[&>nav]:hidden sm:[&>nav]:!flex [&>nav]:z-20"
        )}
        headerClassName="flex items-center p-1 md:p-4 z-20 justify-between overflow-visible"
        contentClassName="text-gray-700 bg-gray-200"
        header={
          <div className="flex items-center justify-between w-full min-w-0 ">
            <div className="flex items-center shrink-0">
              <Button
                variant="minimal"
                className="sm:hidden mr-2 !px-2"
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <List size={24} />
              </Button>
              <span className="pl-1 text-gray-800 font-700 text-lg truncate hidden sm:block shrink-0">
                {navLinks.find(link => link.isActive)?.title}
              </span>
            </div>
            <div className="flex items-center gap-2 ml-auto mr-2 sm:mr-6 z-20 min-w-0">
              <Selector
                options={departments}
                className={classNames(
                  "!min-w-[110px] sm:!min-w-[200px] md:!min-w-[250px] max-w-[160px] sm:max-w-none",
                  isPlantSettlement || isUploaderTask || isCredit || isStock
                    ? "!hidden"
                    : ""
                )}
                value={
                  departments?.find(item => item?.codename === department) ||
                  data?.me?.agent?.current_department
                }
                onChange={(value: any) => setDepartment(value?.codename || "")}
                isClearable={false}
              />
              <FormSelector
                options={viewOptions}
                className={classNames(
                  "hidden",
                  (isTradeRequest || isStockManagement) &&
                    canViewPackagingSelection
                    ? "!flex"
                    : ""
                )}
                intent="primary"
                value={
                  viewOptions.find(
                    opt => opt.id === String(viewPackaging)
                  ) as any
                }
                onChange={(value: any) => setViewPackaging(value?.id)}
                isClearable={false}
              />
            </div>
            <Button
              variant="minimal"
              className="shrink-0 !text-xs sm:!text-sm !px-2 sm:!px-3"
              onClick={() => {
                localStorage.removeItem("token")
                resetFilter()
                resetClient()
                navigate("/")
              }}
            >
              <span className="hidden sm:inline">Logout</span>
              <span className="sm:hidden text-base">⏎</span>
            </Button>
          </div>
        }
      >
        <main
          className={classNames(" text-gray-700 bg-gray-200", contentClassName)}
        >
          <Outlet />
        </main>
        {isSidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 sm:hidden cursor-pointer"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AppShell>
    </AuthorizationProvider>
  )
}

export default DashboardLayout
